import { Copy, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { copyToClipboard } from '@/lib/utils'

const PUBLIC_API_BASE_URL = 'https://mcp.plusefin.com/api'

type AgentSetupCardProps = {
  apiKey?: string
}

export function AgentSetupCard({ apiKey }: AgentSetupCardProps) {
  const { toast } = useToast()
  const displayApiKey = apiKey || 'YOUR_API_KEY'
  const setupPrompt = `Integrate PlusE Finance into this project as a financial data source for AI agents and direct HTTP access.

Use PlusE for market intelligence, company fundamentals, price history, options analytics, institutional holdings, ETF data, earnings, macro indicators, sentiment, news, and prediction tools.

Documentation:
- Overview: https://plusefin.com/docs/
- MCP setup: https://plusefin.com/docs/mcp-client-setup/
- REST API: https://plusefin.com/docs/api/
- Tools reference: https://plusefin.com/docs/tools/

MCP endpoint:
https://mcp.plusefin.com/mcp/?apikey=${displayApiKey}

REST API base URL:
${PUBLIC_API_BASE_URL}

REST authentication:
Authorization: Bearer ${displayApiKey}

When implementing PlusE integration, use the API key provided in this prompt unless the user asks for a different secret-management approach. Do not require users to create or edit environment files just to try the integration. Add typed request helpers, handle non-2xx responses, and choose the appropriate MCP tool or REST endpoint from the docs based on the user's financial data task.`

  const copySetupPrompt = async () => {
    try {
      await copyToClipboard(setupPrompt)
      toast({ title: 'Copied', description: 'Copied to clipboard.' })
    } catch (copyError) {
      toast({ title: 'Copy failed', description: 'Please copy it manually.', variant: 'destructive' })
    }
  }

  return (
    <Card className="overflow-hidden border-0 bg-[#1f2937] text-white shadow-sm shadow-stone-950/10">
      <CardContent className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(360px,520px)] lg:p-6">
        <div className="space-y-4">
          <div className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/70">
            Agent setup
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Set up PlusE for your AI agent</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/70">
              Copy a service-level prompt for Claude Code, Cursor, OpenCode, or Codex. It explains PlusE MCP, REST API, authentication, and documentation links.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              onClick={copySetupPrompt}
              className="bg-white text-stone-950 hover:bg-stone-100"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy setup prompt
            </Button>
            <Button
              variant="outline"
              className="border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white"
              asChild
            >
              <a href="https://plusefin.com/docs/" target="_blank" rel="noreferrer">
                Read docs
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
        <pre className="max-h-[260px] overflow-y-auto whitespace-pre-wrap break-words rounded-2xl bg-white/10 p-4 text-xs leading-6 text-stone-100">
          {setupPrompt}
        </pre>
      </CardContent>
    </Card>
  )
}
